import React, { useState } from 'react';
import { Upload, Check, X, FileText, Loader, Download } from 'lucide-react';

const DocumentItem = ({ document, uploadedDoc, isUploading, onFileUpload }) => {
  const analysis = uploadedDoc?.analysis || uploadedDoc?.ai_feedback || null;
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onFileUpload(document.title, file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/') || file.type === 'application/pdf') {
        onFileUpload(document.title, file);
      } else {
        alert('Please upload an image or PDF file');
      }
    }
  };

  return (
    <div className={`document-item ${uploadedDoc ? 'uploaded' : ''}`}>
      <div className="document-header">
        <div className="document-icon">
          {uploadedDoc ? (
            analysis?.status === 'suitable' ? 
              <Check className="icon-success" /> : 
              <X className="icon-warning" />
          ) : (
            <FileText className="icon-default" />
          )}
        </div>
        
        <div className="document-info">
          <h3>{document.title}</h3>
          <p>{document.description}</p>
          {document.tips && (
            <p className="tips">💡 {document.tips}</p>
          )}
        </div>
        
        <div className="document-status">
          {document.required ? (
            <span className="badge badge-required">Required</span>
          ) : (
            <span className="badge badge-optional">Optional</span>
          )}
        </div>
      </div>

      {uploadedDoc && analysis && (
        <div className="analysis-result">
          <div className={`status-banner status-${analysis.status}`}>
            {analysis.status === 'suitable' ? '✅ Document is suitable' : '❌ Document is not suitable'}
          </div>
          
          {analysis.message && (
            <div className="message">
              <p>{analysis.message}</p>
            </div>
          )}
          
          {analysis.issues?.length > 0 && (
            <div className="issues">
              <strong>Issues:</strong>
              <ul>
                {analysis.issues.map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div 
        className={`upload-section ${isDragOver ? 'drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isUploading ? (
          <div className="processing-state">
            <Loader className="spinner" size={20} />
            <div className="processing-text">Document is being verified…</div>
          </div>
        ) : (
          <>
            <input
              type="file"
              id={`file-${document.title}`}
              onChange={handleFileChange}
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
            />
            <label htmlFor={`file-${document.title}`} className="upload-button">
              <Upload size={20} />
              {uploadedDoc ? 'Replace Document' : 'Upload Document'}
            </label>
            {uploadedDoc?.id && (
              <a 
                className="download-link"
                href={`/api/document/${uploadedDoc.id}`}
                target="_blank" 
                rel="noopener noreferrer"
                title={uploadedDoc.file_name || 'Download file'}
              >
                <Download size={16} />
                <span style={{ marginLeft: 6 }}>Download file</span>
              </a>
            )}
            <div className="drag-drop-hint">
              {isDragOver ? 
                '📎 Drop file here' : 
                '📎 Or drag & drop file here'
              }
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentItem;
